import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import axios from 'axios';
import { API_URL } from '../../config';
import WaveHeader from '../ui/WaveHeader';
import { getCachedData } from '../../utils/prefetch';

export const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [logoutMessage, setLogoutMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const profileDropdownRef = useRef(null);
  const searchRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const navigate = useNavigate();
  const { cartItems } = useCart();
  const { user, logout } = useAuth();
  
  // Calculate total items in cart
  const totalItems = cartItems && Array.isArray(cartItems) 
    ? cartItems.reduce((total, item) => total + item.quantity, 0) 
    : 0;

  // Check if user has admin role
  const isAdmin = user && user.role === 'admin';

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleProfileDropdown = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Prevent navigation when search icon is clicked
    // Just show search suggestions if there's a query
    if (searchQuery.trim()) {
      fetchSearchSuggestions(searchQuery.trim());
    }
  };
  
  // Function to handle 'See all results' button click
  const handleSeeAllResults = () => {
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setShowSuggestions(false);
    }
  };

  const fetchSearchSuggestions = async (query) => {
    if (!query.trim() || query.length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/products`, {
        params: { search: query, limit: 5 }
      });
      
      console.log('Search API response:', response.data);
      
      // Check the structure of the response and extract products accordingly
      const products = response.data.data || 
                      (response.data.products ? response.data.products.data : []) || 
                      [];
                      
      setSearchSuggestions(products);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching search suggestions:', error);
      setSearchSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Add a useRef for the debounce timeout
  const debounceTimeoutRef = useRef(null);

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear the previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Set a new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      fetchSearchSuggestions(query);
    }, 200);
  };

  const handleSuggestionClick = (product) => {
    navigate(`/products/${product.slug}`);
    setSearchQuery('');
    setShowSuggestions(false);
  };

  const handleLogout = () => {
    try {
      // Clear local state and redirect immediately
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Redirect instantly
      navigate('/login', { 
        state: { message: 'Logged out successfully' }
      });

      // Handle server-side logout in background
      logout().catch(error => {
        console.error('Background logout error:', error);
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Fetch categories for dropdown menu
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Fetch all categories with their subcategories
        const categoriesData = await getCachedData('/categories');
        if (Array.isArray(categoriesData)) {
          setCategories(categoriesData);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    
    fetchCategories();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
        setActiveCategory(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileDropdownRef, searchRef, categoryDropdownRef]);

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Logout success message */}
      {logoutMessage && (
        <div className="fixed top-4 right-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm shadow-md z-50">
          {logoutMessage}
        </div>
      )}
      <WaveHeader>
        <div className="container mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-0">
          {/* Logo section - left side on desktop */}
          <div className="flex items-center justify-between md:w-auto md:mr-4">
            {/* Left side - Logo and menu toggle */}
            <div className="flex items-center">
              {/* Mobile menu toggle - with animation */}
              <button 
                className="md:hidden mr-2 text-white focus:outline-none p-1 rounded-md hover:bg-blue-600 transition-colors" 
                onClick={toggleMobileMenu}
                aria-label="Toggle mobile menu"
              >
                <div className="w-6 h-6 relative flex justify-center items-center">
                  <span 
                    className={`absolute h-0.5 w-5 bg-white transform transition-all duration-300 ease-in-out ${isMobileMenuOpen ? 'rotate-45' : '-translate-y-1.5'}`}
                  ></span>
                  <span 
                    className={`absolute h-0.5 bg-white transform transition-all duration-300 ease-in-out ${isMobileMenuOpen ? 'w-0' : 'w-5'}`}
                  ></span>
                  <span 
                    className={`absolute h-0.5 w-5 bg-white transform transition-all duration-300 ease-in-out ${isMobileMenuOpen ? '-rotate-45' : 'translate-y-1.5'}`}
                  ></span>
                </div>
              </button>
              
              {/* Logo */}
              <Link to="/" className="flex items-center">
                <img src="/images/logo.png" alt="M-Mart+" className="h-8" />
              </Link>
            </div>
            {/* Right side icons on mobile */}
            <div className="flex items-center space-x-4 md:hidden">
              {/* Profile/Login */}
              {user ? (
                <Menu as="div" className="relative">
                  {({ open }) => (
                    <>
                      <Menu.Button className="flex items-center hover:text-blue-100">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </Menu.Button>
                      <Transition
                        show={open}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items 
                          static 
                          className="absolute right-0 mt-2 w-48 origin-top-right bg-white rounded-md shadow-lg py-1 z-[1000]"
                        >
                          {isAdmin && (
                            <Menu.Item>
                              {({ active }) => (
                                <Link
                                  to="/admin"
                                  className={`block px-4 py-3 text-base text-gray-700 ${active ? 'bg-gray-100' : ''}`}
                                >
                                  Admin Dashboard
                                </Link>
                              )}
                            </Menu.Item>
                          )}
                          <Menu.Item>
                            {({ active }) => (
                              <Link
                                to="/my-profile"
                                className={`block px-4 py-3 text-base text-gray-700 ${active ? 'bg-gray-100' : ''}`}
                              >
                                My Profile
                              </Link>
                            )}
                          </Menu.Item>
                          <Menu.Item>
                            {({ active }) => (
                              <Link
                                to="/orders"
                                className={`block px-4 py-3 text-base text-gray-700 ${active ? 'bg-gray-100' : ''}`}
                              >
                                My Orders
                              </Link>
                            )}
                          </Menu.Item>
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={handleLogout}
                                className={`block w-full text-left px-4 py-3 text-base text-red-600 ${active ? 'bg-gray-100' : ''}`}
                              >
                                Logout
                              </button>
                            )}
                          </Menu.Item>
                        </Menu.Items>
                      </Transition>
                    </>
                  )}
                </Menu>
              ) : (
                <Link to="/login" className="flex items-center hover:text-blue-100">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </Link>
              )}
              
              {/* Cart */}
              <Link to="/cart" className="flex items-center hover:text-blue-100 relative">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#FFB200] text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center animate-pulse shadow-sm">
                    {totalItems > 9 ? '9+' : totalItems}
                  </span>
                )}
              </Link>
            </div>
          </div>

          {/* Search bar - center on desktop */}
          <div className="flex-grow w-full md:max-w-xl relative" ref={searchRef}>
            <form onSubmit={handleSearch} className="flex w-full">
              <input
                type="text"
                name="searchQuery"
                value={searchQuery}
                onChange={handleSearchInputChange}
                placeholder="Search Products, Brands and Categories"
                className="flex-grow p-2 rounded-l-full border-0 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-[#0071ce] focus:border-transparent"
              />
              <button
                type="submit"
                className="bg-[#0071ce] text-white px-4 py-2 rounded-r-full hover:bg-[#004c91] focus:outline-none focus:ring-2 focus:ring-[#0071ce] flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </form>
            {showSuggestions && searchQuery.trim().length >= 2 && (
              <div className="absolute top-full left-0 w-full bg-white shadow-lg rounded-b-lg overflow-hidden z-50 mt-1 border border-gray-200">
                {isLoading ? (
                  <div className="p-4 text-gray-700 text-center">
                    <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-[#0071ce] border-r-transparent mr-2 align-middle"></div>
                    <span>Searching...</span>
                  </div>
                ) : searchSuggestions.length > 0 ? (
                  <ul className="max-h-80 overflow-y-auto">
                    {searchSuggestions.map((product) => (
                      <li 
                        key={product.id} 
                        className="p-3 text-gray-700 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 flex items-center"
                        onClick={() => handleSuggestionClick(product)}
                      >
                        {(product.image_url || product.image) && (
                          <img 
                            src={product.image_url || product.image || `https://via.placeholder.com/40x40?text=${encodeURIComponent(product.name.charAt(0))}`} 
                            alt={product.name} 
                            className="w-10 h-10 object-cover rounded mr-3"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `https://via.placeholder.com/40x40?text=${encodeURIComponent(product.name.charAt(0))}`;
                            }}
                          />
                        )}
                        <div>
                          <div className="font-medium">{product.name}</div>
                          {(product.price || product.base_price) && (
                            <div className="text-sm text-blue-600 font-bold">
                              ₦{parseFloat(product.price || product.base_price).toLocaleString()}
                              {product.discount_percentage > 0 && (
                                <span className="ml-2 text-xs text-green-600">
                                  {product.discount_percentage}% OFF
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="p-4 text-gray-500 text-center">No products found</div>
                )}
                <div className="p-2 bg-gray-50 border-t border-gray-200 text-center">
                  <button 
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    onClick={handleSeeAllResults}
                  >
                    See all results for "{searchQuery}"
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right side icons - desktop only */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Profile/Login */}
            {user ? (
              <div className="relative group" ref={profileDropdownRef}>
                <button className="flex items-center hover:text-blue-100" onClick={toggleProfileDropdown}>
                  <span className="mr-1 truncate max-w-[100px]">Hello {user.name || user.email?.split('@')[0]}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </button>
                <div className={`fixed top-[60px] right-16 w-48 bg-white rounded-md shadow-lg py-1 z-[1000] ${isProfileOpen ? 'block' : 'hidden'}`}>
                  <Link to="/my-profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">My Profile</Link>
                  <Link to="/orders" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">My Orders</Link>
                  {isAdmin && (
                    <Link to="/admin" className="block px-4 py-2 text-sm text-blue-600 font-medium hover:bg-gray-100">Admin Dashboard</Link>
                  )}
                  <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Logout</button>
                </div>
              </div>
            ) : (
              <Link to="/login" className="flex items-center hover:text-blue-100">
                <span className="mr-1">Login</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </Link>
            )}
            
            {/* Cart */}
            <Link to="/cart" className="flex items-center hover:text-blue-100 relative">
              <span className="mr-1">Cart</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#FFB200] text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center animate-pulse shadow-sm">
                  {totalItems > 9 ? '9+' : totalItems}
                </span>
              )}
            </Link>
            
            {/* Our Stores */}
            <Link to="/stores" className="flex items-center hover:text-blue-100">
              <span className="mr-1">Our Stores</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5m-9 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </Link>
            
            {/* About */}
            <Link to="/about" className="flex items-center hover:text-blue-100">
              <span className="mr-1">About</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </Link>
            
            {/* Help */}
            <Link to="/help" className="flex items-center hover:text-blue-100">
              <span className="mr-1">Help</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </Link>
          </div>
        </div>
      </WaveHeader>
      
      {/* Navigation menu - Hidden on mobile, visible on desktop */}
      <div className="hidden md:block bg-white shadow-sm safe-area-nav">
        <div className="container mx-auto px-4">
          <nav className="flex justify-center md:justify-start py-2">
            <Link to="/" className="px-3 py-1 text-[#2E2E2E] hover:text-[#0071ce] font-medium">Home</Link>
            <Link to="/products" className="px-3 py-1 text-[#2E2E2E] hover:text-[#0071ce] font-medium">Products</Link>
            
            {/* Categories Dropdown */}
            <div className="relative group" ref={categoryDropdownRef}>
              <button 
                className="px-3 py-1 text-[#2E2E2E] hover:text-[#0071ce] font-medium flex items-center"
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
              >
                Categories
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={showCategoryDropdown ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                </svg>
              </button>
              
              {/* Main dropdown */}
              {showCategoryDropdown && (
                <div className="absolute left-0 mt-1 w-64 bg-white rounded-md shadow-lg z-50 flex flex-col max-h-[70vh]">
                  <div className="overflow-y-auto py-1 flex-1">
                    {categories.length > 0 ? (
                      categories.map(category => (
                        <div key={category.id} className="relative group/category">
                          <div 
                            className="flex justify-between items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                            onMouseEnter={() => setActiveCategory(category.id)}
                          >
                            <Link 
                              to={`/categories/${category.slug}`} 
                              className="block w-full"
                              onClick={() => setShowCategoryDropdown(false)}
                            >
                              {category.name}
                            </Link>
                            {category.subcategories && category.subcategories.length > 0 && (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            )}
                          </div>
                          
                          {/* Subcategories flyout menu */}
                          {activeCategory === category.id && category.subcategories && category.subcategories.length > 0 && (
                            <div className="absolute left-full top-0 w-56 bg-white rounded-md shadow-lg py-1 z-50 -ml-1 max-h-[60vh] overflow-y-auto">
                              {category.subcategories.map(subcategory => (
                                <Link 
                                  key={subcategory.id}
                                  to={`/categories/${category.slug}?subcategory=${subcategory.slug}`}
                                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  onClick={() => setShowCategoryDropdown(false)}
                                >
                                  {subcategory.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-sm text-gray-500">Loading categories...</div>
                    )}
                  </div>
                  <div className="border-t border-gray-100 bg-white rounded-b-md">
                    <Link 
                      to="/categories"
                      className="block px-4 py-2 text-sm text-blue-600 font-medium hover:bg-gray-100"
                      onClick={() => setShowCategoryDropdown(false)}
                    >
                      View All Categories
                    </Link>
                  </div>
                </div>
              )}
            </div>
            
            <Link to="/locations" className="px-3 py-1 text-[#2E2E2E] hover:text-[#0071ce] font-medium">Locations</Link>
          </nav>
        </div>
      </div>

      {/* Mobile menu - with slide animation */}
      <div 
        className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setIsMobileMenuOpen(false)}
      ></div>
      <div 
        className={`fixed top-0 left-0 h-full w-3/4 max-w-xs bg-white border-r border-gray-200 shadow-lg z-50 transform transition-transform duration-300 ease-in-out md:hidden safe-area-mobile-menu ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="overflow-y-auto h-full">
          {/* Logo at the very top of the mobile menu with close button */}
          <div className="relative flex items-center justify-center py-4 border-b border-gray-100">
            <img src="/images/logo.png" alt="M-Mart+" className="h-12" />
            <button 
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none rounded-full hover:bg-gray-200 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* User account section */}
          {user ? (
            <div className="px-4 py-3 border-b border-gray-100 bg-white">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg">
                  {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                </div>
                <div className="flex-grow">
                  <div className="font-medium text-gray-800">{user.name || user.email.split('@')[0]}</div>
                  <div className="text-sm text-gray-500">{user.email}</div>
                </div>
              </div>
              <div className="mt-3 flex space-x-2">
                <Link 
                  to="/my-profile" 
                  className="flex-1 py-2 px-3 bg-gray-100 hover:bg-gray-200 text-center text-sm font-medium rounded-md transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  My Profile
                </Link>
                <button 
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex-1 py-2 px-3 bg-red-50 hover:bg-red-100 text-center text-sm font-medium text-red-600 rounded-md transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          ) : (
            <div className="px-4 py-3 border-b border-gray-100 bg-white">
              <Link 
                to="/login" 
                className="block w-full py-2 px-3 bg-blue-600 hover:bg-blue-700 text-center text-white font-medium rounded-md transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Login / Register
              </Link>
            </div>
          )}
          
          <div className="px-4 py-2">
            <nav className="flex flex-col space-y-3">
            <Link to="/" className="py-2 text-[#2E2E2E] hover:text-[#0071ce] font-medium" onClick={() => setIsMobileMenuOpen(false)}>Home</Link>
            <Link to="/products" className="py-2 text-[#2E2E2E] hover:text-[#0071ce] font-medium" onClick={() => setIsMobileMenuOpen(false)}>Products</Link>
            <Link to="/categories" className="py-2 text-[#2E2E2E] hover:text-[#0071ce] font-medium" onClick={() => setIsMobileMenuOpen(false)}>View All Categories</Link>
            <Link to="/locations" className="py-2 text-[#2E2E2E] hover:text-[#0071ce] font-medium" onClick={() => setIsMobileMenuOpen(false)}>Locations</Link>
            <Link to="/about" className="py-2 text-[#2E2E2E] hover:text-[#0071ce] font-medium" onClick={() => setIsMobileMenuOpen(false)}>About Us</Link>
            <Link to="/help" className="py-2 text-[#2E2E2E] hover:text-[#0071ce] font-medium" onClick={() => setIsMobileMenuOpen(false)}>Help</Link>
          </nav>
          </div>
        </div>
      </div>
    </header>
  );
};
